/**
 * W5 — governance, projects and people-admin DTOs.
 *
 * Plain TypeScript shapes, not zod schemas: unlike `types.ts`'s Document/Collection family,
 * nothing here is parsed by `HttpApi` (its `governance`/`projects`/`people` writes are typed
 * refusals — see `http.ts` — because they are KnowIt-platform-specific, the same posture the
 * codebase already takes for `thinkers`/`capabilities`/`ai`). `KnowItApi` maps these fields by
 * hand, exactly like every other namespace in `knowit.ts`. A golden fixture
 * (`knowitGovernance.test.ts`) is what keeps the shapes honest instead.
 */

// ── Projects (§A.3) ──────────────────────────────────────────────────────────

export type ProjectStatus = "active" | "onHold" | "completed" | "archived" | "cancelled";

/** The wire's PascalCase status casing — sent on every write, parsed case-insensitively. */
export const PROJECT_STATUS_WIRE: Record<ProjectStatus, string> = {
  active: "Active",
  onHold: "OnHold",
  completed: "Completed",
  archived: "Archived",
  cancelled: "Cancelled",
};

export function projectStatusFromWire(value: string): ProjectStatus {
  const hit = (Object.entries(PROJECT_STATUS_WIRE) as Array<[ProjectStatus, string]>).find(
    ([, wire]) => wire.toLowerCase() === value.toLowerCase(),
  );
  return hit?.[0] ?? "active";
}

export interface Project {
  id: string;
  teamId: string;
  groupId: string | null;
  name: string;
  description: string | null;
  status: ProjectStatus;
  icon: string | null;
  color: string | null;
  startDate: string | null;
  endDate: string | null;
  ownerId: string | null;
  createdById: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** `W/"<UpdatedAt.Ticks>"` — the optimistic-concurrency token `update()` must echo back. */
  etag: string;
}

export interface ProjectDraft {
  name: string;
  description?: string;
  groupId?: string | null;
  icon?: string | null;
  color?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface ProjectPatch {
  name?: string;
  description?: string | null;
  groupId?: string | null;
  icon?: string | null;
  color?: string | null;
  status?: ProjectStatus;
  startDate?: string | null;
  endDate?: string | null;
}

/** `read` | `read_write` | `admin` — anything else the server silently downgrades to `read`. */
export type GrantLevel = "read" | "read_write" | "admin";

export interface ProjectMembership {
  userId: string;
  permission: GrantLevel;
}

export interface ProjectGroupMembership {
  id: string;
  groupId: string;
  permission: GrantLevel;
  group: { id: string; name: string } | null;
}

/** One row of a `policies.list` batch-resolve — the real per-user ability map (§E-8). */
export interface EntityAbilities {
  id: string;
  abilities: Record<string, boolean>;
}

// ── Docit.Auth administration (§A.1) ─────────────────────────────────────────

export interface AuthIdentity {
  userId: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  teams: Array<{ teamId: string; role: string; status: string }>;
  attributes: Record<string, unknown>;
}

/**
 * The same team, normalised (§E-7): `syncUser().teams[]` carries `teamId`, `getUserTeams()`
 * and `listTeams()` carry `id` for the same field. One shape, everywhere past the adapter.
 */
export interface AuthTeamMembership {
  teamId: string;
  name?: string;
  slug?: string;
  organizationId?: string;
  role: string;
  status: string;
}

export interface AuthTeam {
  id: string;
  name: string;
  slug: string;
  organizationId: string;
  createdAt: string;
}

export interface AuthTeamMember {
  userId: string;
  teamId: string;
  status: string;
  role: string;
  name?: string;
  email?: string;
  teamName?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  attributes: Record<string, unknown>;
  createdAt: string;
}

/** A client-side convenience list (`AVAILABLE_PERMISSIONS`) — the server validates none of it. */
export const AUTH_ROLE_PERMISSIONS = [
  "documents.read",
  "documents.write",
  "documents.delete",
  "documents.share",
  "collections.read",
  "collections.write",
  "collections.delete",
  "users.manage",
  "teams.manage",
  "roles.manage",
  "policies.manage",
  "admin.*",
] as const;

export interface AuthRole {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

export type PolicyEffect = "permit" | "deny";

export interface AuthPolicy {
  id: string;
  name: string;
  description: string | null;
  resourceType: string;
  effect: PolicyEffect;
  priority: number;
  actions: string[];
  conditions: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Organization {
  id: string;
  name: string;
  domain: string | null;
  settingsJson: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  planTier: string;
  planClaimVersion: number;
}

// ── Web sources (§A.4) ────────────────────────────────────────────────────────

export type SearchEngine = "duckduckgo" | "google" | "bing";
export type DomainMode = "off" | "allow" | "deny";

export interface SourcePolicy {
  engineDefault: SearchEngine;
  engineLocked: boolean;
  domainMode: DomainMode;
  domains: string[];
  requireSourceApproval: boolean;
  /** 0 = no limit. */
  maxSourcesPerRun: number;
  /** 0 = tool default. */
  maxCrawlDepth: number;
}

export type SourceApprovalStatus = "pending" | "approved" | "rejected";

export interface SourceApproval {
  id: string;
  domain: string;
  sampleUrl: string | null;
  thinkerId: string | null;
  documentId: string | null;
  status: SourceApprovalStatus;
  createdAt: string;
}

// ── Webhooks (§A.5) ───────────────────────────────────────────────────────────

export interface Webhook {
  id: string;
  teamId: string;
  createdById: string;
  url: string;
  /** Masked (`••••<last4>`) on every read; the real value appears only on create and rotate. */
  secret: string;
  events: string[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastDeliveryAt: string | null;
}

export interface WebhookDraft {
  url: string;
  events: string[];
  enabled?: boolean;
}

export interface WebhookDelivery {
  id: string;
  subscriptionId: string;
  eventType: string;
  status: string;
  attempt: number;
  statusCode: number | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  nextAttemptAt: string | null;
  completedAt: string | null;
}

// ── People: members and groups writes (§A.6, clean-room) ────────────────────

export type MemberRole = "member" | "viewer" | "guest" | "admin";

export interface MemberInvite {
  email: string;
  name?: string;
  role?: MemberRole;
}

export interface InviteResult {
  sent: Array<{ email: string }>;
  users: Array<{ id: string; email: string; name: string }>;
}

export type GroupMemberRole = "member" | "lead";

export interface GroupMembership {
  id: string;
  userId: string;
  groupId: string;
  role: GroupMemberRole;
  user: { id: string; name: string; email: string; avatarUrl: string | null } | null;
}
